import { FC, useState } from 'react';
import {
  Box,
  Button,
  HStack,
  Heading,
  Image,
  Table,
  TableContainer,
  Tbody,
  Td,
  Thead,
  Tr,
  InputGroup,
  InputLeftElement,
  Input,
  useDisclosure,
  IconButton,
  VStack,
} from '@chakra-ui/react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useDebounce } from 'usehooks-ts';
import { useAbility } from '@casl/react';

import PlusIcon from '../../../assets/icons/plus.svg';
import {
  CenteredSpinner,
  FilterBadges,
  FilterDrawer,
  Paginator,
  RoundedBadge,
  TableHeader,
} from '../../../components';
import { ReactComponent as FiltersIcon } from '../../../assets/icons/filters.svg';
import { ReactComponent as SearchIcon } from '../../../assets/icons/search.svg';
import { ReactComponent as RedTrashIcon } from '../../../assets/icons/red-trash.svg';
import { ReactComponent as EditIcon } from '../../../assets/icons/edit.svg';

import { useGetAllRolesQuery } from '../../../services';
import { CompanyStatus } from '../../../models';
import { useTypedSelector } from '../../../hooks';
import { createRolesFilters, rolesInitialFilters } from '../utils';
import { DeleteRoleModal } from '../components';
import { formatDateWithTime } from '../../../utils';
import { AbilityContext, Can } from '../../../providers';
import { SUPER_ADMIN } from '../../../constants';

export const Roles: FC = () => {
  const [searchParams] = useSearchParams();
  const [selectedRole, setSelectedRole] = useState({ id: 0, name: '' });
  const [searchTerm, setSearchTerm] = useState<string>();
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState(rolesInitialFilters);
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const {
    isOpen: isFilterDrawerOpen,
    onOpen: onOpenFilterDrawer,
    onClose: onCloseFilterDrawer,
  } = useDisclosure();

  const {
    isOpen: isDeleteRoleModalOpen,
    onOpen: onOpenDeleteRoleModal,
    onClose: onCloseDeleteRoleModal,
  } = useDisclosure();

  const { userDetails } = useTypedSelector(state => state.auth);

  const { status } = useParams();
  const navigate = useNavigate();

  const { data, isLoading, isFetching, refetch } = useGetAllRolesQuery(
    {
      page,
      basicInfoId: Number(userDetails?.companyBasicInfo.id),
      name: debouncedSearchTerm,
      ...filters,
      status:
        status !== 'all'
          ? (status as CompanyStatus.ACTIVE | CompanyStatus.INACTIVE)
          : undefined,
    },
    { skip: !userDetails }
  );

  const { data: allRoles } = useGetAllRolesQuery(
    {
      page: 0,
      size: 1000,
      basicInfoId: Number(userDetails?.companyBasicInfo.id),
    },
    { skip: !userDetails }
  );

  const ability = useAbility(AbilityContext);

  const handleFilterDrawerClose = (
    filterValues: ReturnType<typeof createRolesFilters>
  ) => {
    onCloseFilterDrawer();
    if (!filterValues) return;

    let filterName: keyof typeof filterValues;

    for (filterName in filterValues) {
      if (filterValues[filterName] === '') delete filterValues[filterName];
    }

    setFilters(filters => ({ ...filters, ...filterValues }));

    void refetch();
  };

  const handleRemoveFilter = (filterKey: keyof typeof filters) => {
    setFilters({ ...filters, [filterKey]: undefined });

    void refetch();
  };

  const rolesForFilter = allRoles?.data.content.map(({ name, id }) => ({
    text: name,
    value: String(id),
  }));

  return (
    <Box h="full">
      {rolesForFilter && isFilterDrawerOpen && (
        <FilterDrawer
          isOpen={isFilterDrawerOpen}
          onClose={handleFilterDrawerClose}
          filters={createRolesFilters(rolesForFilter)}
        />
      )}

      {isDeleteRoleModalOpen && (
        <DeleteRoleModal
          isOpen={isDeleteRoleModalOpen}
          onClose={onCloseDeleteRoleModal}
          role={selectedRole}
        />
      )}

      <VStack align="stretch">
        <HStack justify="space-between" mt="4" align="start">
          <HStack align="baseline">
            <Heading
              color="custom_blue.1"
              fontSize="2xl"
              textTransform="capitalize"
            >
              {status !== 'all' ? status : ''} Roles
            </Heading>
            <Box color="custom_gray.1" fontSize="sm">
              (
              {(data ? String(data?.data.totalElements) : '0').padStart(2, '0')}{' '}
              Roles)
            </Box>
          </HStack>

          <Can I="POST" a="role">
            <Button
              onClick={() => navigate(`/roles/${status || 'all'}/new-role`)}
              bg="custom_orange.2"
              _hover={{ bg: 'custom_orange.2' }}
              color="white"
              fontWeight="semibold"
              fontSize="sm"
            >
              <Image src={PlusIcon} alt="plus icon" boxSize="5" mr="1" />
              Create New Role
            </Button>
          </Can>
        </HStack>

        <VStack
          align="stretch"
          bg="white"
          border="1px"
          borderColor="custom_gray.3"
          my="4"
          p="3"
          gap="3"
          borderRadius="md"
          minH="80vh"
        >
          <HStack py="2">
            <InputGroup flexBasis="45%">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.300" />
              </InputLeftElement>
              <Input
                placeholder="Search"
                onChange={e => setSearchTerm(e.target.value)}
              />
            </InputGroup>

            <Button
              leftIcon={<FiltersIcon />}
              color="custom_blue.1"
              fontWeight="semibold"
              fontSize="sm"
              boxShadow="0 1px 2px 0 rgba(16, 24, 40, 0.05)"
              border="1px"
              borderColor="custom_blue.3"
              bg="none"
              _hover={{ bg: 'none' }}
              onClick={onOpenFilterDrawer}
            >
              Filters
            </Button>
          </HStack>

          {rolesForFilter?.length && (
            <FilterBadges
              onRemoveFilter={handleRemoveFilter}
              filters={filters}
              filterMapper={rolesForFilter}
            />
          )}

          {isLoading || isFetching ? (
            <CenteredSpinner />
          ) : (
            <TableContainer
              display="flex"
              flexDirection="column"
              justifyContent="space-between"
              minH="70vh"
            >
              <Table variant="simple">
                <Thead bg="custom_gray.2">
                  <Tr>
                    <TableHeader title="Role Name" />
                    <TableHeader title="Status" />
                    <TableHeader title="Report To" />
                    <TableHeader title="Activation Date" />
                    <TableHeader title="Last Modifier" />
                    <TableHeader title="Current Users" />
                    {(ability.can('DELETE', 'role') ||
                      ability.can('PUT', 'role')) && (
                      <TableHeader title="Actions" />
                    )}
                  </Tr>
                </Thead>
                <Tbody>
                  {data?.data.content.map(
                    ({
                      name,
                      appRoleStatus,
                      reportTo,
                      id,
                      currentUsers,
                      lastModifier,
                      modificationDate,
                    }) => (
                      <Tr key={id}>
                        <Td
                          color="custom_blue.1"
                          fontWeight="medium"
                          fontSize="xs"
                          border="none"
                        >
                          {name}
                        </Td>
                        <Td border="none">
                          <RoundedBadge colorSchema={appRoleStatus}>
                            {appRoleStatus}
                          </RoundedBadge>
                        </Td>
                        <Td
                          color="custom_blue.1"
                          fontWeight="semibold"
                          fontSize="xs"
                          border="none"
                        >
                          {reportTo?.name}
                        </Td>
                        <Td color="custom_blue.1" fontSize="xs" border="none">
                          {formatDateWithTime(modificationDate)}
                        </Td>
                        <Td
                          color="custom_blue.1"
                          fontWeight="semibold"
                          fontSize="xs"
                          border="none"
                        >
                          {lastModifier}
                        </Td>
                        <Td color="custom_blue.1" fontSize="xs" border="none">
                          {currentUsers}
                        </Td>

                        <Td color="custom_blue.1" fontSize="xs" border="none">
                          <HStack>
                            <Box flexBasis="50%">
                              <Can I="DELETE" a="role">
                                {allowed =>
                                  allowed &&
                                  userDetails?.role.id !== id &&
                                  name !== SUPER_ADMIN &&
                                  (currentUsers === 0 ||
                                    ability.can(
                                      'POST',
                                      'company/teamMember/reassignRole'
                                    )) && (
                                    <IconButton
                                      icon={<RedTrashIcon />}
                                      aria-label="delete role"
                                      bg="transparent"
                                      onClick={() => {
                                        onOpenDeleteRoleModal();
                                        setSelectedRole({ id, name });
                                      }}
                                    />
                                  )
                                }
                              </Can>
                            </Box>
                            <Can I="PUT" a="role">
                              <IconButton
                                icon={<EditIcon />}
                                aria-label="update role"
                                bg="transparent"
                                onClick={() => navigate(String(id))}
                              />
                            </Can>
                          </HStack>
                        </Td>
                      </Tr>
                    )
                  )}
                </Tbody>
              </Table>

              <Paginator
                pageCount={Number(data?.data.totalPages)}
                onPageChange={page => setPage(page)}
              />
            </TableContainer>
          )}
        </VStack>
      </VStack>
    </Box>
  );
};
